import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import PageLoader from '../common/PageLoader';
import { fetchCurrentAgent, logoutAgentAndRedirect } from '../../lib/agentAuth';
import { canAccessPath, roleFromProfile } from '../../lib/agencyAccess';
import { getAgentAccessLevel, getAgentHomePath } from '../../lib/agentOnboarding';
import { getApiErrorMessage, getErrorMessage } from '../../lib/apiMessages';
import { clearAgentToken, getAgentProfileCache, getAgentToken } from '../../lib/tokenStore';

function LoadingState({ message }) {
  return <PageLoader fullScreen message={message} />;
}

function RestrictedState({ profile, onLogout }) {
  const accountStatus = String(profile?.status || '').toLowerCase();

  return (
    <div className="d-flex align-items-center justify-content-center min-vh-100 bg-white px-3">
      <div className="card shadow-sm border-0" style={{ maxWidth: '560px', width: '100%' }}>
        <div className="card-body p-4">
          <h3 className="mb-2">Access restricted</h3>
          <p className="text-secondary mb-3">
            Your account is currently {accountStatus || 'inactive'}. Please contact the operations team to restore portal access.
          </p>
          {profile?.full_name || profile?.utp_full_name ? (
            <div className="mb-3 small text-secondary">
              Signed in as {profile.full_name || profile.utp_full_name} {profile.email || profile.utp_email ? `(${profile.email || profile.utp_email})` : ''}
            </div>
          ) : null}
          <button type="button" className="btn btn-primary" onClick={onLogout}>
            Return to login
          </button>
        </div>
      </div>
    </div>
  );
}

function SessionErrorState({ message, onRetry, onLogout }) {
  return (
    <div className="d-flex align-items-center justify-content-center min-vh-100 bg-white px-3">
      <div className="card shadow-sm border-0" style={{ maxWidth: "560px", width: "100%" }}>
        <div className="card-body p-4">
          <h3 className="mb-2">Unable to verify session</h3>
          <p className="text-secondary mb-3">{message || getErrorMessage("SESSION_VERIFY")}</p>
          <div className="d-flex flex-wrap gap-2">
            <button type="button" className="btn btn-primary" onClick={onRetry}>
              Try again
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={onLogout}>
              Return to login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function isSessionExpired(error) {
  return error?.status === 401;
}

function normalizePath(pathname) {
  return String(pathname || '').replace(/\/+$/, '') || '/';
}

export function RequireAgentAuth({ children }) {
  const location = useLocation();
  const [state, setState] = useState('loading');
  const [profile, setProfile] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let active = true;

    function applyProfile(currentProfile) {
      if (!active || !currentProfile) {
        return;
      }

      setProfile(currentProfile);
      const accessLevel = getAgentAccessLevel(currentProfile);
      if (accessLevel === 'blocked') {
        setState('restricted');
        return;
      }
      if (accessLevel === 'waiting_approval') {
        setState('waiting');
        return;
      }
      if (accessLevel === 'onboarding') {
        setState('onboarding');
        return;
      }
      setState('authenticated');
    }

    async function verifySession() {
      const token = getAgentToken();
      if (!token) {
        if (active) {
          setState('unauthenticated');
        }
        return;
      }

      const cached = getAgentProfileCache(token);
      if (cached.profile) {
        applyProfile(cached.profile);
      }

      try {
        const currentProfile = await fetchCurrentAgent({ force: !cached.profile });
        applyProfile(currentProfile);
      } catch (error) {
        if (!active) {
          return;
        }

        if (isSessionExpired(error)) {
          clearAgentToken();
          setState('unauthenticated');
          return;
        }

        if (cached.profile) {
          applyProfile(cached.profile);
          return;
        }

        setErrorMessage(getApiErrorMessage(error, 'SESSION_VERIFY'));
        setState('error');
      }
    }

    verifySession();

    return () => {
      active = false;
    };
  }, [location.pathname]);

  if (state === 'loading') {
    return <LoadingState message="Checking agent session..." />;
  }

  if (state === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  const path = normalizePath(location.pathname);

  if (state === 'waiting') {
    if (path === '/waiting-approval') {
      return children || <Outlet />;
    }
    return <Navigate to="/waiting-approval" replace />;
  }

  if (state === 'onboarding') {
    if (path === '/onboarding') {
      return children || <Outlet />;
    }
    return <Navigate to="/onboarding" replace />;
  }

  if (state === 'restricted') {
    return (
      <RestrictedState
        profile={profile}
        onLogout={async () => {
          await logoutAgentAndRedirect();
        }}
      />
    );
  }

  if (state === 'error') {
    return (
      <SessionErrorState
        message={errorMessage}
        onRetry={() => window.location.reload()}
        onLogout={async () => {
          await logoutAgentAndRedirect();
        }}
      />
    );
  }

  if (path === '/waiting-approval' || path === '/onboarding') {
    return <Navigate to={getAgentHomePath(profile)} replace />;
  }

  if (!canAccessPath(path, roleFromProfile(profile))) {
    return <Navigate to="/dashboard" replace />;
  }

  return children || <Outlet />;
}

export function RedirectIfAuthenticated({ children }) {
  const [state, setState] = useState('loading');
  const [destination, setDestination] = useState('/packages');

  useEffect(() => {
    let active = true;

    async function verifySession() {
      const token = getAgentToken();
      if (!token) {
        if (active) {
          setState('public');
        }
        return;
      }

      try {
        const profile = await fetchCurrentAgent();
        if (active) {
          setDestination(getAgentHomePath(profile));
          setState('authenticated');
        }
      } catch (error) {
        if (!active) {
          return;
        }

        if (isSessionExpired(error)) {
          clearAgentToken();
        }
        setState('public');
      }
    }

    verifySession();

    return () => {
      active = false;
    };
  }, []);

  if (state === 'loading') {
    return <LoadingState message="Loading agent portal..." />;
  }

  if (state === 'authenticated') {
    return <Navigate to={destination} replace />;
  }

  return children;
}
